import React, { createContext, useContext, useState } from "react";
import { collection, addDoc, query, orderBy, limit, serverTimestamp } from "firebase/firestore";
import { useCollectionData } from "react-firebase-hooks/firestore";
import { db } from "../services/firebase";
import { useAuth } from "./AuthContext";
import { sendTelegramNotification } from "../services/telegramService";

const ChatContext = createContext();

export function useChat() {
  return useContext(ChatContext);
}

export function ChatProvider({ children }) {
  const [error, setError] = useState(null);
  const { currentUser, getUserData } = useAuth();
  const [currentRoomId, setCurrentRoomId] = useState(null); // No default room, must be set explicitly

  // Only use direct messages collection - no public messages
  const messagesRef = currentRoomId 
    ? collection(db, `directMessages/${currentRoomId}/messages`)
    : null;
  
  const messagesQuery = messagesRef 
    ? query(messagesRef, orderBy("createdAt"), limit(100))
    : null;
  
  const [messages, loading, messagesError] = useCollectionData(messagesQuery, { idField: "id" });

  // Send a message to the current direct message room
  const sendMessage = async (text) => {
    if (!currentUser) {
      setError("You must be logged in to send messages");
      return;
    }

    if (!currentRoomId) {
      setError("No chat room selected");
      return;
    }

    const targetCollectionRef = collection(db, `directMessages/${currentRoomId}/messages`);

    try {
      // Fetch user data from Firestore
      const userData = await getUserData(currentUser.uid);
      const userDisplayName = userData?.displayName || currentUser.displayName || "Anonymous";
      const userPhotoURL = userData?.photoURL || currentUser.photoURL || null;
      const userRole = userData?.role || "user"; // Get user role

      // Extract receiver UID from room ID (format: superadmin_email_user_email)
      const roomParts = currentRoomId.split('_');
      let receiverUid = null;
      
      // Find the UID that's not the current user's UID
      for (const part of roomParts) {
        if (part !== currentUser.uid && part.includes('@')) {
          // This is an email, we need to convert it to UID
          // You might need to implement a function to get UID from email
          receiverUid = part;
          break;
        }
      }

      const newMessage = {
        text,
        createdAt: serverTimestamp(),
        uid: currentUser.uid,
        displayName: userDisplayName,
        photoURL: userPhotoURL,
        isAI: false,
        receiverUid: receiverUid, // Always include receiver for direct messages
      };

      await addDoc(targetCollectionRef, newMessage);

      // Optional: Send Telegram notification for direct messages
      // Only send notification if the sender is NOT the superadmin
      if (userRole !== 'super_admin') {
        const notificationText = `<b>New Direct Message from ${newMessage.displayName}</b>\n${text}`;
        await sendTelegramNotification(notificationText);
      }

    } catch (err) {
      setError("Failed to send message: " + err.message);
      console.error("Error sending message:", err);
    }
  };

  // Send AI message to the current room (no longer public-only)
  const sendAIMessage = async (text) => {
    if (!currentRoomId) {
      setError("No chat room selected for AI message");
      return;
    }

    const targetCollectionRef = collection(db, `directMessages/${currentRoomId}/messages`);
    
    try {
      const newMessage = {
        text,
        createdAt: serverTimestamp(),
        uid: "ai-assistant",
        displayName: "AI Assistant",
        photoURL: "/logo192.png", // You can add an AI avatar image
        isAI: true,
      };

      await addDoc(targetCollectionRef, newMessage);
    } catch (err) {
      setError("Failed to send AI message: " + err.message);
      console.error("Error sending AI message:", err);
    }
  };

  // Function to set the current chat room ID (format: superadmin_email_user_email)
  const setChatRoom = (roomId) => {
    setCurrentRoomId(roomId);
    setError(null); // Clear any previous errors when switching rooms
  };

  // Helper function to create room ID from superadmin email and user email
  const createRoomId = (superadminEmail, userEmail) => {
    // Ensure consistent ordering and format
    return `${superadminEmail}_${userEmail}`;
  };

  const value = {
    messages: messages || [], // Ensure messages is always an array
    loading: loading || false,
    error: error || messagesError,
    sendMessage,
    sendAIMessage,
    setChatRoom,
    createRoomId, // Expose helper function
    currentRoomId,
    hasActiveRoom: !!currentRoomId, // Helper to check if a room is selected
  };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
}